import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import {
    Box, Button, Chip, CircularProgress, Grid, IconButton, Menu, MenuItem, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography
} from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import pptxgen from "pptxgenjs";
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import BarChart from "./BarChart";
import PieChart from "./PieChart";
import FilterPanel from "./FilterPanel";
import {
    applyCrossFilter, applyFilters, buildReport, buildTimeline, buildUserList, classifyUsers,
    formTypeLabel, NEW_USER, resolvePeriodStart, RETURN_USER
} from "./analyticsData";
import { fetchUsageReport } from "../../api/analytics";

const cardSx = {
    width: "100%",
    height: "auto",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
    
    "& .MuiCardHeader-title": { fontWeight: 600 }
};

const formTypeColors = ["#161e54", "#3E6B96"];
const FORM_TYPE_BAR_COLORS = { standard: "#10B981", custom: "#14B8A6" };
const USER_MIX_COLORS = { [NEW_USER]: "#10B981", [RETURN_USER]: "#14B8A6" };
const topUsersColors = ["rgb(108 92 188 / 84%)"];
const ptoFormsColors = ["#F97316"];
const customFormsColors = ["#60A5FA"];

const EMPTY_FILTERS = { months: [], formTypes: [], userTypes: [], users: [] };

const PAGE_LABELS = ["Usage Overview", "User Mix & Form Names", "Inactive Users"];


const stripedTableSx = {
    "& thead th": { fontWeight: 600 },
    "& tbody tr:nth-of-type(even)": { backgroundColor: "rgba(62, 107, 150, 0.10)" },
    "& tbody tr:hover": { backgroundColor: "rgba(62, 107, 150, 0.20)" }
};

const CrossFilterNotice = ({ crossFilter, onClear }) => {
    if (!crossFilter) return null;
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Chip
                label={`Filtered by: ${crossFilter.value}`}
                size="small"
                onDelete={onClear}
                sx={{ bgcolor: "rgba(22, 30, 84, 0.08)", fontWeight: 600 }}
            />
            <Typography variant="caption" color="text.secondary">
                Other charts show this selection only — click the mark again to clear.
            </Typography>
        </Box>
    );
};

CrossFilterNotice.propTypes = {
    crossFilter: PropTypes.shape({
        chartId: PropTypes.string,
        field: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onClear: PropTypes.func.isRequired,
};

const KpiStat = ({ label, value, valueColor = "#000000" }) => (
    <Box sx={{ textAlign: "center", px: 3 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
        <Typography variant="h4" sx={{ color: valueColor, fontSize: "2.55rem" }} fontWeight={600}>{value}</Typography>
    </Box>
);

KpiStat.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    valueColor: PropTypes.string,
};

const KpiCard = ({ label, value, valueColor }) => (
    <Box sx={{ ...cardSx, py: 1.5, borderRadius: 1 }}>
        <KpiStat label={label} value={value} valueColor={valueColor} />
    </Box>
);

KpiCard.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    valueColor: PropTypes.string,
};

const useFilteredReport = (rows, filters, crossFilter) => {
    const periodStart = useMemo(() => resolvePeriodStart(rows, filters.months), [rows, filters.months]);
    const userTypeByMail = useMemo(() => classifyUsers(rows, periodStart), [rows, periodStart]);
    const filteredRows = useMemo(
        () => applyFilters(rows, filters, userTypeByMail),
        [rows, filters, userTypeByMail]
    );
    const crossRows = useMemo(
        () => applyCrossFilter(filteredRows, crossFilter, userTypeByMail),
        [filteredRows, crossFilter, userTypeByMail]
    );
    const base = useMemo(() => buildReport(filteredRows, userTypeByMail), [filteredRows, userTypeByMail]);
    const cross = useMemo(() => buildReport(crossRows, userTypeByMail), [crossRows, userTypeByMail]);
    return { base, cross };
};

const chartProps = (report, crossFilter, chartId, seriesKey, field) => {
    const isSource = crossFilter?.chartId === chartId;
    return {
        data: isSource ? report.base[seriesKey] : report.cross[seriesKey],
        selectedKey: isSource ? crossFilter.value : null,
        field
    };
};

const AnalyticsDashboard = () => {
    const page1Ref = useRef(null);
    const page2Ref = useRef(null);
    const page3Ref = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [activePage, setActivePage] = useState(0);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [usageData, setUsageData] = useState(null);
    const [error, setError] = useState(null);
    const [page1Filters, setPage1Filters] = useState(EMPTY_FILTERS);
    const [page2Filters, setPage2Filters] = useState(EMPTY_FILTERS);

    const [page1Cross, setPage1Cross] = useState(null);
    const [page2Cross, setPage2Cross] = useState(null);

    const pageSx = (index) => {
        if (activePage === index) return {display: "block"};
        if (isExporting) {
            // Must stay fully opaque and visible: html2canvas skips any element whose
            // computed opacity is 0 or visibility is hidden, which would export blank pages.
            return {display: "block", position: "absolute", top: 0, left: "-10000px", width: "100%", pointerEvents: "none"};
        }
        return {display: "none"};
    };

    useEffect(() => {
        fetchUsageReport().then(setUsageData).catch(setError);
    }, []);

    const rows = useMemo(() => usageData?.usageRows || [], [usageData]);
    const inactiveUsers = useMemo(() => usageData?.inactiveUsers || [], [usageData]);

    const timeline = useMemo(() => buildTimeline(rows), [rows]);
    const users = useMemo(() => buildUserList(rows), [rows]);

    const page1Report = useFilteredReport(rows, page1Filters, page1Cross);
    const page2Report = useFilteredReport(rows, page2Filters, page2Cross);

    const makeSelectHandler = (setCross, chartId, field) => (datum) =>
        setCross(prev => (prev && prev.chartId === chartId && prev.value === datum.key
            ? null
            : { chartId, field, value: datum.key }));

    const p1Users = chartProps(page1Report, page1Cross, "p1-users", "highestVolumeUsers", "mail");
    const p1FormType = chartProps(page1Report, page1Cross, "p1-form-type", "formTypeVolume", "formType");
    const p1Pto = chartProps(page1Report, page1Cross, "p1-pto", "ptoFormsFrequency", "formCode");
    const p1Custom = chartProps(page1Report, page1Cross, "p1-custom", "customFormsFrequency", "formCode");

    const p2UserMix = chartProps(page2Report, page2Cross, "p2-user-mix", "userMix", "userType");
    const p2FormType = chartProps(page2Report, page2Cross, "p2-form-type", "formTypeVolume", "formType");

    const page1FormTypeVolume = useMemo(
        () => p1FormType.data.map(d => ({
            ...d,
            label: formTypeLabel(d.label),
            ...(FORM_TYPE_BAR_COLORS[d.key] ? { color: FORM_TYPE_BAR_COLORS[d.key] } : {})
        })),
        [p1FormType.data]
    );

    const page2UserMix = useMemo(
        () => p2UserMix.data.map(d => ({
            ...d,
            ...(USER_MIX_COLORS[d.key] ? { color: USER_MIX_COLORS[d.key] } : {})
        })),
        [p2UserMix.data]
    );

    const handleDownload = async (format) => {
        setMenuAnchorEl(null);
        setIsDownloading(true);
        setIsExporting(true);
        try {
            /*"This waits a fixed 100ms after layout, which works on typical
            hardware but may not be enough on slow devices — if charts
            appear blank/cut-off in exports on slow machines, consider
            increasing this delay."*/
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            await new Promise(resolve => setTimeout(resolve, 100));

            const canvases = [];
            for (const ref of [page1Ref, page2Ref, page3Ref]) {
                if (ref.current) {
                    canvases.push(await html2canvas(ref.current, { scale: 2 }));
                }
            }

            if (format === "pdf") {
                const margin = 60;
                let pdf = null;
                canvases.forEach(canvas => {
                    const pageWidth = canvas.width + margin * 2;
                    const pageHeight = canvas.height + margin * 2;
                    if (pdf) {
                        pdf.addPage([pageWidth, pageHeight], "landscape");
                    } else {
                        pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [pageWidth, pageHeight] });
                    }
                    pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, canvas.width, canvas.height);
                });
                pdf.save("docgen-usage-report.pdf");
            } else {
                const pptx = new pptxgen();
                pptx.layout = "LAYOUT_WIDE";
                const slideWidth = 13.33;
                const slideHeight = 7.5;
                const slideMargin = 0.35;
                const availableWidth = slideWidth - slideMargin * 2;
                const availableHeight = slideHeight - slideMargin * 2;
                const availableRatio = availableWidth / availableHeight;

                canvases.forEach(canvas => {
                    const imgRatio = canvas.width / canvas.height;
                    const w = imgRatio > availableRatio ? availableWidth : availableHeight * imgRatio;
                    const h = imgRatio > availableRatio ? availableWidth / imgRatio : availableHeight;
                    pptx.addSlide().addImage({
                        data: canvas.toDataURL("image/png"),
                        x: (slideWidth - w) / 2,
                        y: (slideHeight - h) / 2,
                        w,
                        h
                    });
                });
                await pptx.writeFile({ fileName: "docgen-usage-report.pptx" });
            }
        } finally {
            setIsExporting(false);
            setIsDownloading(false);
        }
    };

    if (error) {
        return (
            <div className="page-content-wrapper">
                <Box sx={{ p: 5 }}>
                    <Typography color="error">Failed to load analytics data. Please try again.</Typography>
                </Box>
            </div>
        );
    }

    if (!usageData) {
        return (
            <div className="page-content-wrapper">
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                    <CircularProgress />
                </Box>
            </div>
        );
    }
    const downloadButton = (
        <Button
            data-html2canvas-ignore="true"
            variant="outlined"
            size="small"
            startIcon={isDownloading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
            onClick={e => setMenuAnchorEl(e.currentTarget)}
            disabled={isDownloading}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
            Download
        </Button>
    );

    return (
        <div className="page-content-wrapper position-relative" style={{ marginTop: "-32px", paddingBottom: "120px" }}>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
                <MenuItem onClick={() => handleDownload("pdf")}>Download as PDF</MenuItem>
                <MenuItem onClick={() => handleDownload("ppt")}>Download as PPT</MenuItem>
            </Menu>

            <Box className="w-100" ref={page1Ref} sx={pageSx(0)}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h2" fontWeight={700}>DocGen Usage Report</Typography>
                    </Grid>
                </Grid>

                <CrossFilterNotice crossFilter={page1Cross} onClear={() => setPage1Cross(null)} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={2}>
                        <FilterPanel
                            timeline={timeline}
                            users={users}
                            filters={page1Filters}
                            setFilters={setPage1Filters}
                            tallTimeline
                        />
                    </Grid>
                    <Grid item xs={12} md={10}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm>
                                <KpiCard label="Forms Generated" value={page1Report.cross.formsGenerated} valueColor="#C2410C" />
                            </Grid>
                            <Grid item xs={12} sm>
                                <KpiCard label="Active Users" value={page1Report.cross.activeUsers} />
                            </Grid>
                            <Grid item xs={12} sm="auto">
                                {downloadButton}
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} sx={{ mt: 0 }}>
                            <Grid item xs={12} md={8}>
                                <JumboCardQuick
                                    title="Highest Volume Users (Top 10)"
                                    sx={{ ...cardSx, height: 320, display: "flex", flexDirection: "column" }}
                                    wrapperSx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, pt: 0, pb: 1, "&:last-child": { pb: 1 } }}
                                >
                                    <BarChart
                                        data={p1Users.data}
                                        selectedKey={p1Users.selectedKey}
                                        onSelect={makeSelectHandler(setPage1Cross, "p1-users", p1Users.field)}
                                        colors={topUsersColors}
                                        orientation="vertical"
                                        fillHeight
                                        height={180}
                                        bottomMargin={45}
                                        leftMargin={70}
                                    />
                                </JumboCardQuick>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <JumboCardQuick
                                    title="Monthly Form Volume - PTO vs Custom"
                                    sx={{ ...cardSx, height: 320, display: "flex", flexDirection: "column" }}
                                    wrapperSx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, pt: 0, pb: 1, "&:last-child": { pb: 1 } }}
                                >
                                    <BarChart
                                        data={page1FormTypeVolume}
                                        selectedKey={p1FormType.selectedKey}
                                        onSelect={makeSelectHandler(setPage1Cross, "p1-form-type", p1FormType.field)}
                                        orientation="vertical"
                                        colors={formTypeColors}
                                        showLegend
                                        fillHeight
                                        height={180}
                                        bottomMargin={45}
                                    />
                                </JumboCardQuick>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <JumboCardQuick title="Most Frequently Generated PTO Forms" sx={cardSx} wrapperSx={{ pt: 0 }}>
                                    <BarChart
                                        data={p1Pto.data}
                                        selectedKey={p1Pto.selectedKey}
                                        onSelect={makeSelectHandler(setPage1Cross, "p1-pto", p1Pto.field)}
                                        colors={ptoFormsColors}
                                        orientation="horizontal"
                                        mirrored
                                    />
                                </JumboCardQuick>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <JumboCardQuick title="Most Frequently Generated Custom Forms" sx={cardSx} wrapperSx={{ pt: 0 }}>
                                    <BarChart
                                        data={p1Custom.data}
                                        selectedKey={p1Custom.selectedKey}
                                        onSelect={makeSelectHandler(setPage1Cross, "p1-custom", p1Custom.field)}
                                        colors={customFormsColors}
                                        orientation="horizontal"
                                        mirrored
                                    />
                                </JumboCardQuick>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

   
            <Box className="w-100" ref={page2Ref} sx={pageSx(1)}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h2" fontWeight={700}>DocGen Usage Report</Typography>
                    </Grid>
                </Grid>

                <CrossFilterNotice crossFilter={page2Cross} onClear={() => setPage2Cross(null)} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={2} sx={{ display: "flex", flexDirection: "column" }}>
                        <FilterPanel
                            timeline={timeline}
                            users={users}
                            filters={page2Filters}
                            setFilters={setPage2Filters}
                            showFormType
                            showUserType
                            showUserList
                            compactTimeline
                            fillHeight
                        />
                    </Grid>
                    <Grid item xs={12} md={10}>

                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm>
                                <KpiCard label="New Users" value={page2Report.cross.newUsers} />
                            </Grid>
                            <Grid item xs={12} sm>
                                <KpiCard label="Returning Users" value={page2Report.cross.returningUsers} />
                            </Grid>
                            <Grid item xs={12} sm>
                                <KpiCard label="Total Forms Generated" value={page2Report.cross.formsGenerated} />
                            </Grid>
                            <Grid item xs={12} sm="auto">
                                {downloadButton}
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} sx={{ mt: 0 }}>
                            <Grid item xs={12} md={5}>
                                <JumboCardQuick title="Monthly User Mix - New vs Returning" sx={{ ...cardSx, mb: 3 }}>
                                    <BarChart
                                        data={page2UserMix}
                                        selectedKey={p2UserMix.selectedKey}
                                        onSelect={makeSelectHandler(setPage2Cross, "p2-user-mix", p2UserMix.field)}
                                        orientation="vertical"
                                    />
                                </JumboCardQuick>
                                <JumboCardQuick title="Total Forms by Form Type" sx={cardSx}>
                                    <PieChart
                                        data={p2FormType.data.map(d => ({ ...d, label: d.label.charAt(0).toUpperCase() + d.label.slice(1) }))}
                                        selectedKey={p2FormType.selectedKey}
                                        onSelect={makeSelectHandler(setPage2Cross, "p2-form-type", p2FormType.field)}
                                        colors={formTypeColors}
                                        showLegend
                                        legendTitle="Form Type"
                                    />
                                </JumboCardQuick>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <JumboCardQuick
                                    title="Forms Generated by Form Name"
                                    sx={{
                                        ...cardSx,
                                        display: "flex",
                                        flexDirection: "column",
                                        height: { xs: "auto", md: 0 },
                                        minHeight: { md: "100%" }
                                    }}
                                    wrapperSx={{
                                        p: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: { md: 1 },
                                        minHeight: 0
                                    }}
                                >
                                    <TableContainer sx={{
                                        maxHeight: { xs: 620, md: "none" },
                                        flex: { md: 1 },
                                        minHeight: 0,
                                        overflow: "auto"
                                    }}>
                                        <Table stickyHeader size="small" sx={stripedTableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Form Name</TableCell>
                                                    <TableCell align="right">Count</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {page2Report.cross.formNameCounts.map((row, index) => (
                                                    <TableRow key={row.label || index} hover>
                                                        <TableCell>{row.label}</TableCell>
                                                        <TableCell align="right">{row.value}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </JumboCardQuick>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <Box className="w-100" ref={page3Ref} sx={pageSx(2)}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h2" fontWeight={700}>DocGen Usage Report</Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={3} alignItems="center" justifyContent="flex-end">
                    <Grid item xs={12} sm={4}>
                        <KpiCard label="Inactive Users" value={inactiveUsers.length} />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {downloadButton}
                    </Grid>
                </Grid>

                <Grid container spacing={3} justifyContent="center" sx={{ mt: 0.25 }}>
                    <Grid item xs={12} md={8}>
                        <JumboCardQuick
                            title="Inactive User List"
                            subheader="Active accounts that have never generated a form"
                            sx={cardSx}
                            wrapperSx={{ p: 0 }}
                        >
                            <TableContainer sx={{ maxHeight: 620 }}>
                                <Table stickyHeader size="small" sx={stripedTableSx}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>E-mail</TableCell>
                                            <TableCell>First Name</TableCell>
                                            <TableCell>Last Name</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {inactiveUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                                    No inactive users
                                                </TableCell>
                                            </TableRow>
                                        ) : inactiveUsers.map((user, index) => (
                                            <TableRow key={user.mail || index} hover>
                                                <TableCell>{user.mail}</TableCell>
                                                <TableCell>{user.firstName}</TableCell>
                                                <TableCell>{user.lastName}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </JumboCardQuick>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                position: "fixed",
                bottom: 50,
                left: "50%",
                transform: "translateX(-50%)",
                width: "fit-content",
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                bgcolor: "background.paper",
                boxShadow: "0 2px 8px rgba(22, 30, 84, 0.15)",
                zIndex: 501
            }}>
                <IconButton
                    size="small"
                    title="Previous page"
                    aria-label="Previous page"
                    disabled={isDownloading || activePage === 0}
                    onClick={() => setActivePage(page => Math.max(0, page - 1))}
                    sx={{ color: "#161e54", "&.Mui-disabled": { color: "#161e54", opacity: 0.3 } }}
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>

                {PAGE_LABELS.map((label, index) => (
                    <Box
                        key={label}
                        component="button"
                        type="button"
                        title={label}
                        aria-label={label}
                        aria-current={activePage === index ? "page" : undefined}
                        disabled={isDownloading}
                        onClick={() => !isDownloading && setActivePage(index)}
                        sx={{
                            width: 12,
                            height: 12,
                            p: 0,
                            borderRadius: "50%",
                            cursor: isDownloading ? "default" : "pointer",
                            border: "2px solid #161e54",
                            bgcolor: activePage === index ? "#161e54" : "transparent",
                            opacity: activePage === index ? 1 : 0.55,
                            transition: "background-color 0.2s, opacity 0.2s",
                            "&:hover": { opacity: 1 }
                        }}
                    />
                ))}

                <IconButton
                    size="small"
                    title="Next page"
                    aria-label="Next page"
                    disabled={isDownloading || activePage === PAGE_LABELS.length - 1}
                    onClick={() => setActivePage(page => Math.min(PAGE_LABELS.length - 1, page + 1))}
                    sx={{ color: "#161e54", "&.Mui-disabled": { color: "#161e54", opacity: 0.3 } }}
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
        </div>
    );
};

export default AnalyticsDashboard;
